import { useEffect } from 'react';

/**
 * Shows an unmistakable banner when the admin panel is running on staging, so
 * nobody confuses it with production. Gated on the hostname at RUNTIME (not a
 * build flag), so the exact same build shows the banner on
 * staging.app.notiz.se and stays invisible on app.notiz.se — there is no way
 * for it to leak onto production. An explicit REACT_APP_ENV_LABEL=staging also
 * forces it on, as a belt-and-suspenders override.
 */
export function isStaging() {
  try {
    const host = (window.location.hostname || '').toLowerCase();
    if (host.includes('staging')) return true;
    if ((process.env.REACT_APP_ENV_LABEL || '').toLowerCase() === 'staging') return true;
  } catch (e) {
    // window may be unavailable during SSR/tests — treat as not staging.
  }
  return false;
}

export default function StagingBanner() {
  const staging = isStaging();

  useEffect(() => {
    if (!staging) return undefined;
    // Push the app down so the fixed bar never covers its header, and mark the
    // browser tab too. Both are restored on unmount.
    const prevPadding = document.body.style.paddingTop;
    document.body.style.paddingTop = '30px';
    const prevTitle = document.title;
    if (!prevTitle.startsWith('[STAGING]')) {
      document.title = `[STAGING] ${prevTitle}`;
    }
    return () => {
      document.body.style.paddingTop = prevPadding;
      document.title = prevTitle;
    };
  }, [staging]);

  if (!staging) return null;

  return (
    <div
      role="status"
      aria-label="Staging environment"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 30,
        lineHeight: '30px',
        background:
          'repeating-linear-gradient(45deg,#b45309,#b45309 12px,#92400e 12px,#92400e 24px)',
        color: '#fff',
        fontWeight: 700,
        fontSize: 13,
        letterSpacing: '0.14em',
        textAlign: 'center',
        zIndex: 2147483647,
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
        textTransform: 'uppercase',
        boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
        userSelect: 'none',
      }}
    >
      ⚠ STAGING — testmiljö (staging.app.notiz.se), inte produktion
    </div>
  );
}
